import { Component, type ErrorInfo, type ReactNode } from 'react'

import { applyLocale, resolveLocale, translate } from '../lib/i18n'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * The last line of defence against a white screen.
 *
 * A render error in a food diary is not a rare event — a malformed entry, a
 * missing nutrient, a date that crossed a timezone boundary. The user gets a
 * readable screen with a way out; the console gets the stack.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Error reporting needs a vendor decision and a data-residency position
    // (SUAP-0008 § Out of scope), so this stays in the console for now.
    console.error('[suap] render error', error, info.componentStack)
  }

  private readonly reload = (): void => {
    window.location.assign('/')
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    // The one place that resolves the locale directly instead of through
    // `useT()`: this is a class component, and more to the point it has to
    // render when everything below it has failed — including, potentially, the
    // provider. `translate` takes the locale as an argument for exactly this.
    const locale = resolveLocale()
    // Stamp it too, not just translate with it: this screen renders when the
    // provider may have failed, and without this <html lang> keeps whatever
    // index.html shipped — so the error screen is the one place AC-4 did not
    // hold. Found in review.
    applyLocale(locale)

    return (
      <main className="mx-auto flex min-h-full max-w-[430px] flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-semibold">{translate(locale, 'error.title')}</h1>
        <p className="opacity-70">{translate(locale, 'error.body')}</p>
        <button
          type="button"
          onClick={this.reload}
          className="rounded-full border border-current px-4 py-2 font-medium"
        >
          {translate(locale, 'action.retry')}
        </button>
      </main>
    )
  }
}
